import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  listCreatorSeries,
  listCreatorEpisodesForSeries,
  type CreatorSeriesSummary,
  type CreatorEpisodeSummary,
} from '@/lib/api/creator'

type EpisodeStatus = string

interface EpisodeRow {
  id: string
  title: string
  seriesId: string
  seriesTitle: string
  durationMinutes: number | null
  status: EpisodeStatus
  publishedAt: string | null
}

interface SeriesOption {
  id: string
  title: string
}

export function CreatorEpisodes() {
  const [episodes, setEpisodes] = useState<EpisodeRow[]>([])
  const [seriesOptions, setSeriesOptions] = useState<SeriesOption[]>([])
  const [seriesFilter, setSeriesFilter] = useState<'all' | string>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | EpisodeStatus>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const series: CreatorSeriesSummary[] = await listCreatorSeries()
        if (cancelled) return
        const options = series.map((s) => ({ id: s.id, title: s.title }))
        setSeriesOptions(options)

        const allEpisodes: CreatorEpisodeSummary[][] = await Promise.all(
          series.map((s) => listCreatorEpisodesForSeries(s.id)),
        )
        if (cancelled) return
        const flat: EpisodeRow[] = []
        series.forEach((s, idx) => {
          const eps = allEpisodes[idx] || []
          eps.forEach((ep) => {
            flat.push({
              id: ep.id,
              title: ep.title,
              seriesId: s.id,
              seriesTitle: s.title,
              durationMinutes: ep.duration ? Math.round(ep.duration / 60) : null,
              status: ep.status,
              publishedAt: ep.releasedAt ?? null,
            })
          })
        })
        setEpisodes(flat)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load episodes')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const statusOptions = useMemo(() => {
    const set = new Set<EpisodeStatus>()
    episodes.forEach((e) => {
      if (e.status) set.add(e.status)
    })
    return ['all', ...Array.from(set)]
  }, [episodes])

  const filteredEpisodes = useMemo(
    () =>
      episodes.filter((e) => {
        if (seriesFilter !== 'all' && e.seriesId !== seriesFilter) return false
        if (statusFilter !== 'all' && e.status !== statusFilter) return false
        return true
      }),
    [episodes, seriesFilter, statusFilter],
  )

  const retryProcessing = (id: string) => {
    setEpisodes((prev) => prev.map((e) => (e.id === id ? e : e)))
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Episodes</h1>
          <p className="mt-1 text-sm text-gray-400">
            See all of your episodes across series with a quick view of status and publish dates,
            powered by your real creator data.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-3 text-xs">
        <div className="inline-flex items-center gap-2">
          <span className="text-gray-400">Series:</span>
          <div className="inline-flex rounded-full bg-white/5 p-1">
            <button
              type="button"
              onClick={() => setSeriesFilter('all')}
              className={`px-3 py-1 rounded-full ${
                seriesFilter === 'all'
                  ? 'bg-[#D4AF37] text-black'
                  : 'text-gray-300 hover:bg-white/5'
              }`}
            >
              All
            </button>
            {seriesOptions.map((series) => (
              <button
                key={series.id}
                type="button"
                onClick={() => setSeriesFilter(series.id)}
                className={`px-3 py-1 rounded-full ${
                  seriesFilter === series.id
                    ? 'bg-[#D4AF37] text-black'
                    : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {series.title}
              </button>
            ))}
          </div>
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="text-gray-400">Status:</span>
          <div className="inline-flex rounded-full bg-white/5 p-1">
            {statusOptions.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status === 'all' ? 'all' : status)}
                className={`px-3 py-1 rounded-full capitalize ${
                  statusFilter === status
                    ? 'bg-[#D4AF37] text-black'
                    : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-800 bg-black/40">
        <div className="grid grid-cols-[minmax(0,2.1fr)_minmax(0,1.4fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 border-b border-gray-800">
          <div>Episode</div>
          <div>Series</div>
          <div>Duration</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-400">Loading episodes…</div>
        ) : filteredEpisodes.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400">
            No episodes match the selected filters.
          </div>
        ) : (
          <div className="divide-y divide-gray-900">
            {filteredEpisodes.map((e) => (
              <div
                key={e.id}
                className="grid grid-cols-[minmax(0,2.1fr)_minmax(0,1.4fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-4 py-3 text-sm items-center"
              >
                <div>
                  <p className="font-medium text-white line-clamp-1">{e.title}</p>
                  <p className="text-xs text-gray-500">
                    {e.publishedAt
                      ? `Published ${new Date(e.publishedAt).toLocaleDateString()}`
                      : 'Not yet published'}
                  </p>
                </div>
                <div className="text-sm text-gray-300 line-clamp-1">{e.seriesTitle}</div>
                <div className="text-sm text-gray-300">
                  {e.durationMinutes != null ? `${e.durationMinutes} min` : '—'}
                </div>
                <div className="text-xs capitalize">
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 bg-gray-900/40 text-gray-300 border border-gray-700">
                    {e.status || 'unknown'}
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    size="xs"
                    variant="secondary"
                    className="border-gray-800 text-gray-300 hover:bg-white/5"
                  >
                    View in series
                  </Button>
                  {e.status === 'failed' && (
                    <Button
                      size="xs"
                      variant="secondary"
                      className="border-emerald-700 text-emerald-300 hover:bg-emerald-900/20"
                      onClick={() => retryProcessing(e.id)}
                    >
                      Retry processing
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

